import { SmartBatteryAlertStatus } from '../../config/alertConstants';
import { request, RequestInitWithRetry } from '../../../lib/request';
import { ISortResult, VIEW } from '../common';
import { get } from 'lodash';

export interface IBatteryParams {
  statusType: any,
  limit: number,
  offset: number,
  sort?: ISortResult,
  search: string,
  pinnedId?: string,
  filters: IBatteryFilters
}

export interface IBatteryFilters {
  groups?: string[],
  homeLocations?: string[],
  underWarranty: boolean,
  batteryPartNumbers?: string[],
  models?: string[],
  status?: string[],
  alertStatus?: string[],
  groups_negate?: boolean,
  homeLocations_negate?: boolean,
  batteryPartNumbers_negate?: boolean,
  models_negate?: boolean
}

export const batteryNegateKeys = ['groups_negate', 'homeLocations_negate', 'batteryPartNumbers_negate', 'models_negate'] as const;
export type BatteryNegateKey = typeof batteryNegateKeys[number];

// Forward only the negate flags that are explicitly enabled, mirroring the Core devices list behaviour
export const getBatteryNegateParams = (filters: IBatteryFilters = {} as IBatteryFilters): Partial<Record<BatteryNegateKey, boolean>> => {
  return batteryNegateKeys.reduce((acc, key) => {
    if (filters[key]) {
      acc[key] = true;
    }
    return acc;
  }, {} as Partial<Record<BatteryNegateKey, boolean>>);
};

export interface ISmartBatteryStatus {
  id: string,
  serialNumber: string,
  manufacturer?: string,
  updated?: string,
  underWarranty?: boolean,
  partNumber?: string,
  batteryStatus?: string,
  health?: string,
  cycleCount?: number,
  averageDischarge?: number,
  averageDischargeStatus?: string,
  averageDischargeTime?: number,
  firstSeen?: string,
  mfd?: string,
  cycleCountStatus?: string,
  healthStatus?: string,
  deviceInfo?: {
    deviceName?: string,
    manufacturer?: string,
    model?: string,
    serialNumber?: string,
    imei?: string,
    assetTag?: string,
    group?: string[],
    homeLocationId?: string,
    homeLocation?: {
      id: string,
      name: string
    }
  }
}

export interface IBatteriesWithCount {
  red: number,
  orange: number,
  green: number,
  all: number
}


export interface IBatteryAverageDischargeData {
  date: number,
  level: number
}

export interface SmartBatteryAlertLevel {
red: number,
yellow: number
}

type AlertStatusCount = {
  [alert in SmartBatteryAlertStatus]: SmartBatteryAlertLevel
};

export type ISmartBatteryAlertCount = {
  all: number
} & AlertStatusCount;

export interface ISmartBatteryHealthIntervalStats {
  ZeroToForty: number,
  FortyToFifty: number,
  FiftyToSixty: number,
  SixtyToSeventy: number,
  SeventyToEighty: number,
  EightyToNinety: number,
  NinetyToHundred: number
}

export interface ISmartBatteryAvgDischargeIntervalStats {
  ZeroToTen: number,
  TenToTwenty: number,
  TwentyToThirty: number,
  ThirtyToForty: number,
  FortyToFifty: number,
  FiftyToSixty: number,
  SixtyToSeventy: number,
  SeventyToEighty: number,
  EightyToNinety: number,
  NinetyToHundred: number,
  HundredAbove: number
}

export interface ISmartBatteryFirstSeenIntervalStats {
  months0To1: number,
  months1To2: number,
  months2To3: number,
  months3To6: number,
  months6To12: number,
  over12Months: number
}

export interface ISmartBatteryBatteryAgeIntervalStats {
  months0To6: number,
  months6To12: number,
  months12To18: number,
  months18To24: number,
  months24To36: number,
  over36Months: number,
  unknown: number
}

export interface ISmartBatteryCycleCountIntervalStats {
  cycles0To100: number,
  cycles100To200: number,
  cycles200To300: number,
  cycles300To400: number,
  cycles400To500: number,
  cycles500To600: number,
  cycles600To700: number,
  cycles700AndAbove: number
}

export interface ISmartBatteryAvgDischargeTimeIntervalStats {
  hour0To1: number,
  hour1To2: number,
  hour2To3: number,
  hour3To4: number,
  hour4To5: number,
  hour5To6: number,
  hour6To7: number,
  hour7To8: number,
  hour8To9: number,
  hour9To10: number,
  hour10AndAbove: number
}

export class BatteryCentricService {
  // For endpoints that support status and alertStatuses filters (new endpoints)
  private getFilterParams = (filters: IBatteryFilters) => {
    const alertStatuses = filters?.alertStatus ? filters.alertStatus.reduce((acc, id) => {
      const [status, filter] = id.split('-');
      acc[status] = [...(acc[status] || []), filter];
      return acc;
    }, {} as { [s: string]: string[] }) : undefined;
    return {
      groups: filters?.groups,
      homeLocations: filters?.homeLocations,
      underWarranty: filters?.underWarranty,
      batteryPartNumbers: filters?.batteryPartNumbers,
      models: filters?.models,
      status: filters?.status,
      alertStatuses,
      ...getBatteryNegateParams(filters)
    };
  };

  // For legacy endpoints that don't support status and alertStatuses filters
  private getLegacyFilterParams = (filters: IBatteryFilters) => {
    return {
      groups: filters?.groups,
      homeLocations: filters?.homeLocations,
      underWarranty: filters?.underWarranty,
      batteryPartNumbers: filters?.batteryPartNumbers,
      models: filters?.models
    };
  };

  public get = (options: RequestInitWithRetry) => async (params: IBatteryParams): Promise<ISmartBatteryStatus[]> => {
    let result;
    const input = {
      path: `/api/batteryEssentials/getSmartBatteriesStatus`,
      qs: { statusType: params.statusType === VIEW.all ? undefined : params.statusType, limit: params.limit, offset: params.offset, pinnedId: params.pinnedId },
      body: {
        sort: get(params, 'sort'),
        search: get(params, 'search'),
        ...this.getFilterParams(params.filters)
      },
      method: 'post'
    };

    result = await request(input, options);
    return result.smartBatteries.nodes;
  };

  public export = (options: RequestInitWithRetry) => async (params: IBatteryParams): Promise<any> => {
    const input = {
      path: `/api/batteryEssentials/exportSmartBatteriesStatus`,
      qs: { statusType: params.statusType === VIEW.all ? undefined : params.statusType, limit: params.limit, offset: params.offset },
      body: {
        sort: get(params, 'sort'),
        search: get(params, 'search'),
        ...this.getFilterParams(params.filters)
      },
      method: 'post'
    };

    return await request(input, options);
  };

  public getBatteryEstateData = (options: RequestInitWithRetry) => async (filters: IBatteryFilters, search?: string): Promise<IBatteriesWithCount> => {
    const input = {
      path: `/api/batteryEssentials/getSmartBatteriesWithCount`,
      body: {
        search: search || undefined,
        ...this.getLegacyFilterParams(filters)
      },
      method: 'post'
    };

    return await request(input, options);
  };

  public getBatteryEstateDataWithFilters = (options: RequestInitWithRetry) => async (filters: IBatteryFilters, search?: string): Promise<number> => {
    const input = {
      path: `/api/batteryEssentials/getSmartBatteriesCountWithFilters`,
      body: {
        search: search || undefined,
        ...this.getFilterParams(filters)
      },
      method: 'post'
    };

    return await request(input, options);
  };

  public getSmartBatteryDetails = (options: RequestInitWithRetry) => async (id: string): Promise<any> => {
    const input = {
      path: `/api/batteryEssentials/getSmartBattery`,
      qs: { id },
      method: 'get'
    };

    return await request(input, options);
  };

  public getSmartBatteryLast30DaysDischargeTrend = (options: RequestInitWithRetry) => async (id: string, dates: { from: number, to: number }): Promise<IBatteryAverageDischargeData[]> => {
    const input = {
      path: `/api/batteryEssentials/last30DaysBatteryDischargeTrend`,
      qs: { id, from: dates.from, to: dates.to },
      method: 'get'
    };
    return await request(input, options);
  };

  public getSmartBatteryLast24HourBatteryLevel = (options: RequestInitWithRetry) => async (id: string, to: number): Promise<any> => {
    const input = {
      path: `/api/batteryEssentials/last24HoursBatteryLevel`,
      qs: { id, to },
      method: 'get'
    };
    return await request(input, options);
  };

  public getSmartBatteryAlertsCount = (options: RequestInitWithRetry) => async (): Promise<ISmartBatteryAlertCount> => {
    const input = {
      path: '/api/batteryEssentials/smartBatteryAlertsCount',
      method: 'get'
    };
    return request(input, options);
  };

  public getSmartBatteryHealthIntervalStats = (options: RequestInitWithRetry) => async (): Promise<ISmartBatteryHealthIntervalStats> => {
    const input = {
      path: '/api/batteryEssentials/last30DaysHealthIntervalStats',
      method: 'get'
    };
    return request(input, options);
  };

  public getLast30DaysAverageDischargeIntervalStats = (options: RequestInitWithRetry) => async (): Promise<ISmartBatteryAvgDischargeIntervalStats> => {
    const input = {
      path: `/api/batteryEssentials/last30DaysAverageDischargeIntervalStats`,
      method: 'get'
    };

    return await request(input, options);
  };

  public getFirstSeenIntervalStats = (options: RequestInitWithRetry) => async (): Promise<ISmartBatteryFirstSeenIntervalStats> => {
    const input = {
      path: `/api/batteryEssentials/batteriesFirstSeenIntervalStats`,
      method: 'get'
    };

    return await request(input, options);
  };

  public getLast30DaysBatteryAgeIntervalStats = (options: RequestInitWithRetry) => async (): Promise<ISmartBatteryBatteryAgeIntervalStats> => {
    const input = {
      path: `/api/batteryEssentials/last30DaysBatteriesAgeIntervalStats`,
      method: 'get'
    };

    return await request(input, options);
  };

  public getLast30DaysCycleCountIntervalStats = (options: RequestInitWithRetry) => async (): Promise<ISmartBatteryCycleCountIntervalStats> => {
    const input = {
      path: `/api/batteryEssentials/last30DaysCycleCountIntervalStats`,
      method: 'get'
    };

    return await request(input, options);
  };

  public getLast30DaysBatteryDischargeTimeIntervalStats = (options: RequestInitWithRetry) => async (): Promise<ISmartBatteryAvgDischargeTimeIntervalStats> => {
    const input = {
      path: `/api/batteryEssentials/last30DaysBatteryDischargeTimeIntervalStats`,
      method: 'get'
    };

    return await request(input, options);
  };
}
